// O DESAFIO DA SEMANA: convite, montagem e resultado.
//
// A partida em si nao esta aqui. O desafio roda dentro do MESMO jogo -- painel,
// elenco, tatica, mercado, partida ao vivo --, porque uma tela paralela seria
// uma segunda interface para manter, e ela envelheceria sozinha. O que este
// arquivo faz e o comeco e o fim: apresentar o cenario da semana, montar o
// estado, e fechar a conta quando as 10 rodadas acabam.

import React, { useEffect, useState } from 'react';
import { newLeague, createCareer, advanceRound, autoLineup } from '../engine/career.js';
import { montarDesafio, pontuar } from '../engine/desafio.js';
import { apiUrl } from './api.js';
import { marcar } from './analytics.js';

/* As funcoes do motor entregues ao montador. Ver o comentario em
   `montarDesafio`: e injecao para nao fechar ciclo de import. */
const MOTOR = { newLeague, createCareer, advanceRound, autoLineup };

/* ⚠️ O token vai no GET tambem, e nao so no envio do resultado. O desafio e
   de quem tem cadastro (ver a rota em server.js): sem o cabecalho aqui, quem
   esta logado tomaria 401 na propria tela de convite. */
export async function buscarDesafio(token) {
  const r = await fetch(apiUrl('/api/desafio'), {
    cache: 'no-store',
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || 'Nao consegui falar com o servidor.');
  }
  return r.json();
}

export async function enviarResultado(corpo, token) {
  const r = await fetch(apiUrl('/api/desafio'), {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(corpo),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || 'Nao consegui enviar.');
  return j;
}

/** Monta o estado do desafio. Pesado (~300 ms): quem chama mostra "montando". */
export function prepararDesafio(desafio) {
  const m = montarDesafio(desafio, MOTOR);
  /* A marca que diz ao resto do jogo que isto nao e uma carreira. O `apply` do
     App olha para ela para escolher a chave de save e para NAO mandar nada
     para a nuvem -- o save na nuvem e da carreira, nao do desafio. */
  m.estado.desafio = {
    semana: desafio.semana,
    cenarioId: desafio.cenarioId,
    titulo: desafio.titulo,
    rodadaInicial: m.rodadaInicial,
    rodadaFinal: m.rodadaInicial + desafio.rodadas - 1,
    clubeId: m.clubeId,
    campanhaInicial: m.campanhaInicial,
    // Guardado para a pontuacao no fim: sem isto seria preciso resimular tudo.
    standingsInicial: { ...m.estado.standings[m.clubeId] },
    roundInicial: m.estado.round,
  };
  return m;
}

/** O desafio acabou? */
export const desafioTerminou = (state) =>
  !!state?.desafio && state.round > state.desafio.rodadaFinal;

/** A pontuacao final, a partir do que ficou guardado no proprio estado. */
export function resultadoDoDesafio(state) {
  const d = state.desafio;
  const inicial = {
    standings: { [d.clubeId]: d.standingsInicial },
    round: d.roundInicial,
  };
  return pontuar(inicial, state, d.clubeId);
}

// ============================================================== O CONVITE ==
export function DesafioConvite({ onJogar, onVoltar, account }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [montando, setMontando] = useState(false);

  useEffect(() => {
    let vivo = true;
    buscarDesafio(account?.token)
      .then((d) => { if (vivo) setDados(d); })
      .catch((e) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [account?.token]);

  const jogar = () => {
    if (!dados) return;
    setMontando(true);
    marcar('desafio_iniciado', { semana: dados.desafio.semana });
    /* Cede um quadro antes de montar. Sao ~300 ms de simulacao travando a
       thread; sem isto o botao fica afundado e parece que o clique nao pegou
       -- o mesmo cuidado que `onAdvance` ja toma no App. */
    setTimeout(() => {
      try {
        onJogar(prepararDesafio(dados.desafio), dados.desafio);
      } catch (e) {
        setErro(String(e?.message || e));
        setMontando(false);
      }
    }, 30);
  };

  return (
    <>
      <div className="topbar">
        <div className="inner">
          <div className="row" style={{ gap: 8 }}>
            <button className="ghost sm" onClick={onVoltar}>voltar</button>
            <div className="brand">desafio <b>da semana</b></div>
          </div>
          <div className="meta">{dados?.desafio?.semana || ''}</div>
        </div>
      </div>

      <div className="wrap">
        {erro && <div className="card tight neg small">{erro}</div>}
        {!dados && !erro && <div className="card dim small">Carregando o desafio...</div>}

        {dados && (
          <>
            <div className="card desafio-cabeca">
              <p className="kicker">• Todo mundo joga a mesma liga</p>
              <h1 style={{ margin: '4px 0 8px' }}>{dados.desafio.titulo}</h1>
              <p className="muted" style={{ margin: 0, fontSize: 14 }}>{dados.desafio.resumo}</p>
              <div className="row wrap" style={{ gap: 7, marginTop: 14 }}>
                <span className="pill">NBB</span>
                <span className="pill">{dados.desafio.rodadas} rodadas</span>
                <span className="pill">gratis</span>
              </div>
            </div>

            <div className="card tight small dim">
              O mesmo elenco, o mesmo calendario e os mesmos adversarios para
              todo mundo nesta semana. O que muda e so o que voce fizer.
            </div>

            <button className="cta" onClick={jogar} disabled={montando}>
              <span className="ic">🏀</span>
              <span>
                <span className="tt">{montando ? 'Montando a liga...' : 'Jogar o desafio'}</span>
                <span className="ss">
                  {montando ? 'simulando ate a rodada de partida' : 'leva alguns minutos'}
                </span>
              </span>
              <span className="arrow">›</span>
            </button>

            <h2>Ranking da semana</h2>
            {dados.ranking.length === 0 && (
              <div className="card dim small">Ninguem terminou ainda. Seja o primeiro.</div>
            )}
            {dados.ranking.length > 0 && (
              <div className="card tight">
                <div className="scroll">
                  <table className="tabela">
                    <thead>
                      <tr><th>#</th><th>Tecnico</th><th>Campanha</th><th>Saldo</th></tr>
                    </thead>
                    <tbody>
                      {dados.ranking.map((r) => (
                        <tr key={`${r.posicao}-${r.nome}`}>
                          <td>{r.posicao}</td>
                          <td>{r.nome}</td>
                          <td>{r.vitorias}V-{r.derrotas}D</td>
                          <td>{r.saldo > 0 ? `+${r.saldo}` : r.saldo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="small dim" style={{ marginTop: 8 }}>
                  {dados.total} tecnico(s) nesta semana
                  {dados.meuLugar ? ` · voce esta em ${dados.meuLugar}o` : ''}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

// ============================================================= O RESULTADO ==
export function DesafioResultado({ state, onSair, account }) {
  const d = state.desafio;
  const r = resultadoDoDesafio(state);
  const [nome, setNome] = useState(() => account?.nome || account?.email?.split('@')[0] || '');
  const [enviado, setEnviado] = useState(null);
  const [erro, setErro] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const clube = state.teams.find((t) => t.id === d.clubeId)?.name || '';

  const enviar = async () => {
    setEnviando(true);
    setErro(null);
    try {
      const resposta = await enviarResultado({
        nome,
        vitorias: r.vitorias,
        derrotas: r.derrotas,
        saldo: r.saldo,
        posicaoFinal: r.posicaoFinal,
      }, account?.token);
      marcar('desafio_enviado', { semana: d.semana, vitorias: r.vitorias });
      setEnviado(resposta);
    } catch (e) {
      setErro(String(e?.message || e));
    }
    setEnviando(false);
  };

  return (
    <>
      <div className="topbar">
        <div className="inner">
          <div className="row" style={{ gap: 8 }}>
            <div className="brand">desafio <b>da semana</b></div>
          </div>
          <div className="meta">{d.semana}</div>
        </div>
      </div>

      <div className="wrap">
        <div className="card desafio-cabeca center">
          <p className="kicker">• {d.titulo}</p>
          <div className="desafio-placar">{r.vitorias}<span>-{r.derrotas}</span></div>
          <p className="muted" style={{ margin: '2px 0 0' }}>
            com o {clube} · saldo {r.saldo > 0 ? `+${r.saldo}` : r.saldo}
            {r.posicaoFinal ? ` · terminou em ${r.posicaoFinal}o` : ''}
          </p>
        </div>

        {!enviado && (
          <div className="card">
            <h3>Entrar no ranking</h3>
            <label className="small dim" htmlFor="desafio-nome">Como voce quer aparecer</label>
            <input
              id="desafio-nome"
              value={nome}
              maxLength={24}
              placeholder="Seu nome"
              onChange={(e) => setNome(e.target.value)}
              style={{ marginTop: 6 }}
            />
            {erro && <div className="small neg" style={{ marginTop: 8 }}>{erro}</div>}
            <button
              className="primary"
              style={{ width: '100%', marginTop: 12 }}
              disabled={enviando || nome.trim().length < 2}
              onClick={enviar}
            >
              {enviando ? 'Enviando...' : 'Enviar meu resultado'}
            </button>
            <p className="small dim" style={{ marginTop: 8, marginBottom: 0 }}>
              Um resultado por pessoa. Enviar de novo substitui o anterior.
            </p>
          </div>
        )}

        {enviado && (
          <div className="card tight pos small">
            Resultado enviado. Volte ao desafio para ver o ranking.
          </div>
        )}

        <button className="cta dark" onClick={onSair}>
          <span className="ic">‹</span>
          <span>
            <span className="tt">Sair do desafio</span>
            <span className="ss">sua carreira continua onde estava</span>
          </span>
        </button>
      </div>
    </>
  );
}
